import {parseJSON} from 'date-fns';
import styles from '../RequestDetails.module.scss';
import Card from 'components/Card/Card';
import CardContent from 'components/CardContent';
import DateTimePicker from 'components/DateTimePicker';
import {useEffect, useState} from 'react';
import Select from 'components/Select';
import {format} from 'date-fns';
import {components} from 'react-select';
import ReactSwitch from 'react-switch';
import {PreferenceValues} from '../useRequestDetails';
import {Prescription} from 'types';
import api from 'api';
import {useMutation} from 'react-query';
import toast from 'react-hot-toast';
import {get} from 'lodash';

interface Props {
  preference_date: string | Date | null;
  preference_time: string;
  isUrgent?: boolean;
  flag_reason?: string | null;
  drug?: Prescription | null;
  onUpdatePreference: (values: PreferenceValues) => void;
  refetchRequestDetails: () => void;
}

const options = [
  {value: 'morning', label: 'Morning'},
  {value: 'afternoon', label: 'Afternoon'},
  {value: 'evening', label: 'Evening'},
];

const updateRequestDrug = async ({
  drugId,
  body,
}: {
  drugId?: number;
  body: {refrigerate: boolean};
}) => {
  if (!drugId) {
    return;
  }
  const {data} = await api.patch(`/requests/drug/${drugId}/`, body);
  return data;
};

function PreferenceTime({
  refetchRequestDetails,
  preference_date,
  preference_time,
  onUpdatePreference,
  isUrgent,
  flag_reason,
  drug,
}: Props) {
  const createDateFromString = (dateString: string): Date => {
    const [year, month, day] = dateString.split('-').map(Number);
    // Create date with local time components to avoid UTC conversion issues
    return new Date(year, month - 1, day, 12, 0, 0);
  };

  const [date, setDate] = useState<Date | null>(() => {
    if (!preference_date) return null;
    return typeof preference_date === 'string'
      ? createDateFromString(preference_date)
      : preference_date;
  });
  const [time, setTime] = useState<string>(preference_time);
  const [isRefrigerate, setIsRefrigerate] = useState<boolean>(
    Boolean(drug?.refrigerate)
  );

  const [urgencyReasonValue, setUrgencyReasonValue] = useState<string>(
    flag_reason || ''
  );
  const [isUrgentDelivery, setIsUrgentDelivery] = useState<boolean>(
    Boolean(isUrgent)
  );

  const {mutateAsync: updateRequestDrugMutation} = useMutation(
    'update_request_drug',
    updateRequestDrug,
    {
      onSuccess: refetchRequestDetails,
      onError: (error) => {
        toast.error(
          get(error, 'response.data.error_message', 'Something went wrong')
        );
        setIsRefrigerate(drug?.refrigerate || false);
      },
    }
  );
  useEffect(() => {
    if (typeof preference_date === 'string') {
      setDate(createDateFromString(preference_date));
    } else if (preference_date instanceof Date) {
      setDate(preference_date);
    }
  }, [preference_date]);

  useEffect(() => {
    if (flag_reason !== undefined) {
      setUrgencyReasonValue(flag_reason || '');
    }
  }, [flag_reason]);

  const onChangeData = (date: any) => {
    setDate(date);

    onUpdatePreference({
      preference_date: date ? format(date, 'yyyy-MM-dd') : null,
      preference_time: time,
    });
  };

  const onChangeTimer = (nextVal: any) => {
    setTime(nextVal?.value || '');
    onUpdatePreference({
      preference_date: date ? format(date, 'yyyy-MM-dd') : null,
      preference_time: nextVal?.value || null,
    });
  };

  return (
    <Card>
      <CardContent px="sm" pt="none" pb="sm">
        <div className={styles.times}>
          <div>
            <div className={styles.label}>Preference date:</div>
            <div className={styles.calendarRoot}>
              <DateTimePicker
                showTimeSelect={false}
                dateFormat="MM/dd/yy E"
                value={date ? parseJSON(date) : undefined}
                onChange={onChangeData}
              />
            </div>
          </div>
          <div>
            <div className={styles.label}>Preference time:</div>
            <Select
              className={styles.select}
              onChange={onChangeTimer}
              placeholder={
                <span className="select-placeholder-text">Select time</span>
              }
              isClearable
              components={{
                DropdownIndicator: (props) =>
                  !time ? <components.DropdownIndicator {...props} /> : null,
              }}
              value={
                time
                  ? {value: time, label: time[0].toUpperCase() + time.slice(1)}
                  : null
              }
              options={options}
              size="md"
            />
          </div>
        </div>
        <div className={styles.times}>
          <div className={styles.label}>Refrigerate: </div>
          <div className={styles.label}>
            <ReactSwitch
              onColor="#0072b3"
              onChange={async (checked) => {
                setIsRefrigerate(checked);
                await updateRequestDrugMutation({
                  drugId: drug?.id,
                  body: {refrigerate: checked},
                });
              }}
              checked={isRefrigerate}
              checkedIcon={false}
              uncheckedIcon={false}
              handleDiameter={16}
              height={20}
              width={40}
            />
          </div>
        </div>
        <div className={styles.times}>
          <div className={styles.label}>Flag: </div>
          <div className={styles.label}>
            <ReactSwitch
              onColor="#0072b3"
              onChange={() => {
                setIsUrgentDelivery(!isUrgentDelivery);
                onUpdatePreference({
                  is_urgent_delivery: !isUrgentDelivery,
                  flag_reason: null,
                });
              }}
              checked={isUrgentDelivery}
              checkedIcon={false}
              uncheckedIcon={false}
              handleDiameter={16}
              height={20}
              width={40}
            />
          </div>
        </div>
        {Boolean(isUrgent) && (
          <div className={styles.times}>
            <textarea
              className={styles.urgencyReasonTextarea}
              placeholder="Enter Flag Reason"
              value={urgencyReasonValue}
              onChange={(e) => setUrgencyReasonValue(e.target.value)}
              onBlur={() => {
                onUpdatePreference({flag_reason: urgencyReasonValue});
              }}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default PreferenceTime;
